import React, { useEffect, useState } from "react";
import AdminLayout from "../admin/AdminDashboardLayout";
import Button from "../../utils/Button";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useApiMutation } from "../../hooks/useMutation";
import { ApiRequestOptions } from "../../utils/api";
import { toast } from "react-hot-toast";
import MessagingDisclosureModal from "../../components/MessagingDisclosureModal";
import { useMessagingDisclosure } from "../../hooks/useMessagingDisclosure";
import Messaging from "./settings/Messaging";
import { CopyAll } from "@mui/icons-material";
import { Copy, Info } from "lucide-react";
import { useLocation } from "react-router-dom";
import { ORG_TYPES } from "../../utils/Constant";

interface EmbedData {
  embedId: string;
  partnerIdentifier: string;
  partnerName: string;
  enabled: boolean;
  label: string | null;
  allowedDomains: string | null;
  requestCount: number;
  lastSeenAt: string | null;
  createdAt: string;
  updatedAt: string;
  scriptLoaderUrl: string;
  iframeUrl: string;
}

interface PartnerProfile {
  name: string;
  orgType: string;
  primaryEmail: string;
  contact: string;
  website: string;
  partnerIdentifier: string;
  messagingEnabled?: boolean;
  voteByMailReminderEnabled?: boolean;
  enabledModules?: string[];
}



export default function PartnerSettings() {
  const location = useLocation();

  const resolveInitialTab = () => {
    const stateTab = (location.state as { tab?: string } | null)?.tab;
    const searchTab = new URLSearchParams(location.search).get('tab');
    const tab = stateTab || searchTab;
    if (tab === 'messaging' || tab === 'dlc') {
      return tab as 'messaging' | 'dlc';
    }
    return 'accounts';
  };

  const [activeTab, setActiveTab] = useState<'accounts' | 'messaging' | 'dlc'>(resolveInitialTab());
  const [showDisclosureModal, setShowDisclosureModal] = useState(false);
  const [showScriptInfoModal, setShowScriptInfoModal] = useState(false);
  const [showIframeInfoModal, setShowIframeInfoModal] = useState(false);

  const { data, isLoading } = useApiQuery<{
    data: PartnerProfile;
  }>({
    queryKey: ["partner", "me"],
    request: { method: "GET", url: "/partner" } as ApiRequestOptions,
  });

  const initial: PartnerProfile = {
    name: data?.data?.name || "",
    orgType: data?.data?.orgType || "",
    primaryEmail: data?.data?.primaryEmail || "",
    contact: data?.data?.contact || "",
    website: data?.data?.website || "",
    partnerIdentifier: data?.data?.partnerIdentifier || "",
    messagingEnabled: data?.data?.enabledModules?.includes("MESSAGING_REMINDER_TOOLS") || false,
    voteByMailReminderEnabled: data?.data?.enabledModules?.includes("VOTE_BY_MAIL_REMINDER_TOOLS") || false,
  };
  const [profile, setProfile] = useState<PartnerProfile>(initial);


  const partnerId = data?.data?.partnerIdentifier;
  const { acknowledgeDisclosure } = useMessagingDisclosure(partnerId);

  // Embed functionality
  const { data: embedData, isLoading: embedLoading, refetch: refetchEmbed } = useApiQuery<{
    data: EmbedData;
  }>({
    queryKey: ["embed", partnerId],
    request: {
      method: "GET",
      url: `/partner/embed/${partnerId}`
    } as ApiRequestOptions,
    enabled: !!partnerId,
    retry: false,
  });

  const generateEmbedMutation = useApiMutation({
    onSuccess: () => {
      toast.success("Embed link generated successfully");
      refetchEmbed();
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to generate embed link";
      toast.error(errorMessage);
    }
  });

  const handleCopyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleGenerateEmbed = () => {
    generateEmbedMutation.mutate({
      method: "POST",
      url: "/partner/embed",
    });
  };

  useEffect(() => {
    setProfile(initial);
  }, [data]);

  useEffect(() => {
    const nextTab = resolveInitialTab();
    setActiveTab((prev) => (prev === nextTab ? prev : nextTab));
  }, [location.search, location.state]);

  const updateMutation = useApiMutation({
    onSuccess: () => toast.success("Profile updated"),
    onError: () => toast.error("Update failed")
  });

  const messagingUpdateMutation = useApiMutation({
    onSuccess: (_, variables) => {
      toast.success("Messaging settings updated");
      // Update the local state manually based on the enabled value
      const enabled = (variables as any).data?.enabled;
      setProfile(prev => ({
        ...prev,
        messagingEnabled: enabled
      }));

      window.dispatchEvent(new CustomEvent('messagingSettingsChanged', {
        detail: { enabled }
      }));
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to update messaging settings";
      toast.error(errorMessage);
    }
  });

  const voteByMailReminderUpdateMutation = useApiMutation({
    onSuccess: (_, variables) => {
      toast.success("Vote-by-Mail reminder settings updated");
      // Update the local state manually based on the enabled value
      const enabled = (variables as any).data?.enabled;
      setProfile(prev => ({
        ...prev,
        voteByMailReminderEnabled: enabled
      }));
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to update Vote-by-Mail reminder settings";
      toast.error(errorMessage);
    }
  });

  const handleMessagingToggle = () => {
    if (profile.messagingEnabled) {
      messagingUpdateMutation.mutate({
        method: "PUT",
        url: "/partner/enabled-modules/messaging-reminder-tools",
        data: { enabled: false },
      });
    } else {
      setShowDisclosureModal(true);
    }
  };

  const handleVoteByMailReminderToggle = () => {
    voteByMailReminderUpdateMutation.mutate({
      method: "PUT",
      url: "/partner/enabled-modules/vote-by-mail-reminder-tools",
      data: { enabled: !profile.voteByMailReminderEnabled },
    });
  };

  const handleEnableMessaging = () => {
    acknowledgeDisclosure();
    setShowDisclosureModal(false);

    messagingUpdateMutation.mutate({
      method: "PUT",
      url: "/partner/enabled-modules/messaging-reminder-tools",
      data: { enabled: true },
    });
  };

  const handleCloseModal = () => {
    setShowDisclosureModal(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({
      method: "PUT",
      url: "/partner/update",
      data: profile,
    });
  };

  return (
    <AdminLayout role="PARTNER">
      <div className="w-full mx-auto">
        <div className="bg-white max-w-md rounded-full shadow-sm border border-gray-200 mb-6">
          <div className="flex w-full space-x-1 p-1">
            {[
              { id: 'accounts', label: 'Accounts' },
              { id: 'messaging', label: 'Messaging System' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as 'accounts' | 'messaging' | 'dlc')}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-full font-medium text-sm transition-all duration-200 ${activeTab === tab.id
                  ? 'bg-red-600 text-white shadow-lg transform scale-105'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
              >
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 min-h-[500px]">
          {activeTab === 'accounts' && (
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-4">
                <h2 className="text-2xl font-bold text-gray-900">Account Settings</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Manage your organization profile and account preferences
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                className="space-y-8"
              >
                {/* Organization Details */}
                <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                    Organization Details
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Organization Name
                      </label>
                      <input
                        type="text"
                        value={profile.name}
                        onChange={(e) =>
                          setProfile({ ...profile, name: e.target.value })
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      // required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Organization Type
                      </label>
                      <select
                        value={profile.orgType}
                        onChange={(e) =>
                          setProfile({ ...profile, orgType: e.target.value })
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      // required
                      >
                        <option value="">Select type</option>
                        {ORG_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {profile.primaryEmail && (<div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Primary Contact Email
                      </label>
                      <input
                        type="email"
                        value={profile.primaryEmail}
                        onChange={(e) =>
                          setProfile({ ...profile, primaryEmail: e.target.value })
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      // required
                      />
                    </div>)}
                    {profile.contact && (<div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Contact
                      </label>
                      <input
                        type="email"
                        value={profile.contact}
                        onChange={(e) =>
                          setProfile({ ...profile, contact: e.target.value })
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      // required
                      />
                    </div>)}
                    {profile.website && (<div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Organisation Website
                      </label>
                      <input
                        type="url"
                        value={profile.website}
                        onChange={(e) =>
                          setProfile({ ...profile, website: e.target.value })
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      // required
                      />
                    </div>)}
                  </div>
                </div>


                <div className="flex justify-end mb-10">
                  <Button
                    type="submit"
                    label={updateMutation.isPending ? "Saving..." : "Save Changes"}
                    variant="primary"
                  />
                </div>
              </form>


              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="w-2 h-2 bg-green-600 rounded-full mr-2"></span>
                  Messaging Settings
                </h3>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-semibold text-gray-700">
                        SMS/Email Messaging
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Enable messaging features for your organization
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleMessagingToggle}
                      disabled={messagingUpdateMutation.isPending}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${profile.messagingEnabled
                        ? 'bg-red-600'
                        : 'bg-gray-200'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <span className="sr-only">Toggle messaging</span>
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${profile.messagingEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                  {profile.messagingEnabled && (
                    <div className="mt-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      ✓ Messaging is enabled for your organization
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-semibold text-gray-700">
                        Automated Vote-by-Mail Reminder
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Enable automated vote-by-mail reminder for voters in your organisation
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleVoteByMailReminderToggle}
                      disabled={voteByMailReminderUpdateMutation.isPending}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${profile.voteByMailReminderEnabled
                        ? 'bg-red-600'
                        : 'bg-gray-200'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <span className="sr-only">Toggle vote-by-mail reminder</span>
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${profile.voteByMailReminderEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                  {profile.voteByMailReminderEnabled && (
                    <div className="mt-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      ✓ Vote by mail reminder is enabled for your organization
                    </div>
                  )}
                </div>
              </div>

              {/* Partner ID */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="w-2 h-2 bg-slate-600 rounded-full mr-2"></span>
                  Partner ID Confirmation
                </h3>
                <div className="bg-white rounded-lg p-6 border-2 border-dashed border-gray-300 text-center">
                  <div className="text-2xl font-mono tracking-wider text-gray-900 font-bold">
                    {profile.partnerIdentifier || "—"}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">Save this ID for future reference and system access</p>
                </div>
              </div>

              {/* Embed Section */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                  Embed Links
                </h3>

                {embedLoading ? (
                  <div className="bg-white rounded-lg p-6 border border-gray-200 text-center">
                    <div className="text-sm text-gray-500">Loading embed information...</div>
                  </div>
                ) : embedData?.data ? (
                  <div className="bg-white rounded-lg p-6 border border-gray-200 space-y-4">
                    {!embedData.data.enabled && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-700">
                        <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                        Embed feature has been disabled for your organization, kindly reach out to support@123-vote.com
                      </div>
                    )}

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Script Loader URL
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={embedData?.data.scriptLoaderUrl}
                            readOnly
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                          />
                          <button
                            onClick={() => handleCopyToClipboard(embedData.data.scriptLoaderUrl, "Script Loader URL")}
                            disabled={!embedData.data.enabled}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${embedData.data.enabled
                              ? 'text-slate-700 hover:text-slate-900'
                              : 'text-slate-300 cursor-not-allowed'
                              }`}
                          >
                            <Copy />
                          </button>
                          <button
                            onClick={() => setShowScriptInfoModal(true)}
                            className="px-3 py-2 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors"
                            title="View integration instructions"
                          >
                            <Info className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Iframe URL
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={embedData.data.iframeUrl}
                            readOnly
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                          />
                          <button
                            onClick={() => handleCopyToClipboard(embedData.data.iframeUrl, "Iframe URL")}
                            disabled={!embedData.data.enabled}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${embedData.data.enabled
                              ? 'text-slate-700 hover:text-slate-900'
                              : 'text-slate-300 cursor-not-allowed'
                              }`}
                          >
                            <Copy />
                          </button>
                          <button
                            onClick={() => setShowIframeInfoModal(true)}
                            className="px-3 py-2 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors"
                            title="View integration instructions"
                          >
                            <Info className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-6 border flex flex-col items-center justify-center border-gray-200 text-center">
                    <div className="text-sm text-gray-600 mb-4">
                      Your organisation does not have an embed link yet.
                    </div>
                    <Button
                      type="button"
                      label={generateEmbedMutation.isPending ? "Generating..." : "Generate embed link"}
                      variant="primary"
                      onClick={handleGenerateEmbed}
                      disabled={generateEmbedMutation.isPending}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'messaging' &&
            <Messaging />
          }


        </div>

        {/* Messaging Disclosure Modal */}
        <MessagingDisclosureModal
          isOpen={showDisclosureModal}
          onClose={handleCloseModal}
          onEnableMessaging={handleEnableMessaging}
          partnerId={partnerId}
        />

        {/* Script Loader Info Modal */}
        {showScriptInfoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-blue-100">
              {/* Header */}
              <div className="bg-gradient-to-r from-slate-800 to-neutral-700 p-6 text-white">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Script Loader Integration</h3>
                      <p className="text-blue-100 text-sm">Advanced JavaScript embedding guide</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowScriptInfoModal(false)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
                <div className="space-y-6">
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-blue-100">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h4 className="font-bold text-gray-900">Overview</h4>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      The script loader allows you to embed the 123-Vote widget seamlessly into your website using JavaScript.
                      This method provides better performance and more customization options compared to iframes.
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="px-3 py-1.5  bg-neutral-500 rounded-lg">
                          <span className="text-white font-bold text-sm">1</span>
                        </div>
                        <h4 className="font-bold text-gray-900">Add the Script Tag</h4>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-3">Copy the script loader URL and add it to your HTML file, preferably just before the closing {"</body>"} tag:</p>
                    <div className="relative group">
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm border border-gray-700">
                        {`<script src="${embedData?.data.scriptLoaderUrl || 'YOUR_SCRIPT_LOADER_URL'}" async></script>`}
                      </div>
                      <button
                        onClick={() => handleCopyToClipboard(`<script src="${embedData?.data.scriptLoaderUrl || 'YOUR_SCRIPT_LOADER_URL'}" async></script>`, "Script Tag")}
                        className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Copy code"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-slate-50 to-neutral-50 rounded-xl p-5 border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="px-3 py-1.5 bg-slate-500 rounded-lg">
                          <span className="text-white font-bold text-sm">2</span>
                        </div>
                        <h4 className="font-bold text-gray-900">Create the Widget Container</h4>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-3">Add a div element where you want the vote widget to appear:</p>
                    <div className="relative group">
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm border border-gray-700">
                        {`<div id="123-vote-widget"></div>`}
                      </div>
                      <button
                        onClick={() => handleCopyToClipboard('<div id="123-vote-widget"></div>', "Widget Container")}
                        className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Copy code"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-neutral-50 to-orange-50 rounded-xl p-5 border border-neutral-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="px-3 py-1.5 bg-neutral-500 rounded-lg">
                          <span className="text-white font-bold text-sm">3</span>
                        </div>
                        <h4 className="font-bold text-gray-900">Initialize the Widget (Optional)</h4>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-3">If you need to customize the widget or load it dynamically, you can initialize it manually:</p>
                    <div className="relative group">
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-xs border border-gray-700 overflow-x-auto">
                        {"`<script>\n  window.addEventListener('load', function() {\n    if (window.VoteWidget) {\n      VoteWidget.init({\n        containerId: '123-vote-widget',\n        partnerId: '" + (embedData?.data.partnerIdentifier || 'YOUR_PARTNER_ID') + "',\n        // Optional customization options\n        theme: 'default',\n        language: 'en'\n      });\n    }\n  });\n</script>`"}
                      </div>
                      <button
                        onClick={() => handleCopyToClipboard(`<script>\n  window.addEventListener('load', function() {\n    if (window.VoteWidget) {\n      VoteWidget.init({\n        containerId: '123-vote-widget',\n        partnerId: '${embedData?.data.partnerIdentifier || 'YOUR_PARTNER_ID'}',\n        // Optional customization options\n        theme: 'default',\n        language: 'en'\n      });\n    }\n  });\n</script>`, "Widget Initialization")}
                        className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Copy code"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-5 border border-yellow-200">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="p-2 bg-yellow-500 rounded-lg">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <h4 className="font-bold text-gray-900">Important Notes</h4>
                    </div>
                    <ul className="space-y-2">
                      <li className="flex items-start space-x-2">
                        <span className="text-yellow-500 mt-1">•</span>
                        <span className="text-gray-600">The script loads asynchronously to prevent blocking your page load</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-yellow-500 mt-1">•</span>
                        <span className="text-gray-600">Make sure the container div has the exact ID: <code className="bg-yellow-100 px-2 py-1 rounded text-yellow-800 font-mono text-xs">123-vote-widget</code></span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-yellow-500 mt-1">•</span>
                        <span className="text-gray-600">The widget will automatically initialize when the script loads</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-yellow-500 mt-1">•</span>
                        <span className="text-gray-600">For single-page applications, you may need to reinitialize the widget on route changes</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="p-2 bg-green-500 rounded-lg">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <h4 className="font-bold text-green-800">Pro Tip</h4>
                    </div>
                    <p className="text-green-700 text-sm">
                      Place the script tag at the end of your body tag for optimal page load performance.
                      The widget will appear automatically once the script loads.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Iframe Info Modal */}
        {showIframeInfoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gradient-to-br from-slate-50 to-slate-50 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-slate-100">
              {/* Header */}
              <div className="bg-gradient-to-r from-slate-800 to-gray-700 p-6 text-white">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0l1 16h8l1-16M9 9h6m-6 4h6" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Iframe Integration</h3>
                      <p className="text-slate-100 text-sm">Simple embedding with HTML iframes</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowIframeInfoModal(false)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
                <div className="space-y-6">
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="p-2 bg-slate-100 rounded-lg">
                        <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h4 className="font-bold text-gray-900">Overview</h4>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      The iframe method provides a simple way to embed the 123-Vote widget in your website.
                      It's easy to implement and works well with most website builders and CMS platforms.
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-slate-50 to-neutral-50 rounded-xl p-5 border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="px-3 py-1.5 bg-slate-500 rounded-xl">
                          <span className="text-white font-bold text-sm">1</span>
                        </div>
                        <h4 className="font-bold text-gray-900">Basic Implementation</h4>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-3">Copy the iframe URL and use it in an iframe tag where you want the widget to appear:</p>
                    <div className="relative group">
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm border border-gray-700 overflow-x-auto">
                        {`<iframe src="${embedData?.data.iframeUrl || 'YOUR_IFRAME_URL'}" 
        width="100%" 
        height="600" 
        frameborder="0" 
        >
</iframe>`}
                      </div>
                      <button
                        onClick={() => handleCopyToClipboard(`<iframe src="${embedData?.data.iframeUrl || 'YOUR_IFRAME_URL'}" \n        width="100%" \n        height="600" \n        frameborder="0" \n        >\n</iframe>`, "Basic Iframe")}
                        className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Copy code"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-neutral-50 to-rose-50 rounded-xl p-5 border border-neutral-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="px-3 py-1.5  bg-neutral-500 rounded-xl">
                          <span className="text-white font-bold text-sm">2</span>
                        </div>
                        <h4 className="font-bold text-gray-900">Responsive Implementation</h4>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-3">For better responsive design, wrap the iframe in a container:</p>
                    <div className="relative group">
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm border border-gray-700 overflow-x-auto">
                        {"`<div style=\"position: relative; width: 100%; height: 600px;\">\n  <iframe src=\"" + (embedData?.data.iframeUrl || 'YOUR_IFRAME_URL') + "\" \n          style=\"position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;\"\n          frameborder=\"0\" \n          scrolling=\"no\">\n  </iframe>\n</div>`"}
                      </div>
                      <button
                        onClick={() => handleCopyToClipboard(`<div style="position: relative; width: 100%; height: 600px;">\n  <iframe src="${embedData?.data.iframeUrl || 'YOUR_IFRAME_URL'}" \n          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;"\n          frameborder="0" \n          >\n  </iframe>\n</div>`, "Responsive Iframe")}
                        className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Copy code"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-rose-50 to-orange-50 rounded-xl p-5 border border-rose-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="px-3 py-1.5  bg-gray-500 rounded-xl">
                          <span className="text-white font-bold text-sm">3</span>
                        </div>
                        <h4 className="font-bold text-gray-900">Advanced Styling</h4>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-3">You can add custom styling to better integrate the iframe with your site:</p>
                    <div className="relative group">
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-xs border border-gray-700 overflow-x-auto">
                        {"`<style>\\n  .vote-widget-container {\\n    border-radius: 8px;\\n    box-shadow: 0 2px 10px rgba(0,0,0,0.1);\\n    overflow: hidden;\\n    background: white;\\n  }\\n  .vote-widget-container iframe {\\n    border: none;\\n  }\\n</style>\\n\\n<div class=\"vote-widget-container\">\\n  <iframe src=\"" + (embedData?.data.iframeUrl || 'YOUR_IFRAME_URL') + "\" \\n          width=\"100%\" \\n          height=\"600\" \\n          frameborder=\"0\" \\n          scrolling=\"no\">\\n  </iframe>\\n</div>`"}
                      </div>
                      <button
                        onClick={() => handleCopyToClipboard(`<style>\n  .vote-widget-container {\n    border-radius: 8px;\n    box-shadow: 0 2px 10px rgba(0,0,0,0.1);\n    overflow: hidden;\n    background: white;\n  }\n  .vote-widget-container iframe {\n    border: none;\n  }\n</style>\n\n<div class="vote-widget-container">\n  <iframe src="${embedData?.data.iframeUrl || 'YOUR_IFRAME_URL'}" \n          width="100%" \n          height="600" \n          frameborder="0" \n          >\n  </iframe>\n</div>`, "Advanced Styling")}
                        className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Copy code"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-5 border border-yellow-200">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="p-2 bg-yellow-500 rounded-lg">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <h4 className="font-bold text-gray-900">Important Notes</h4>
                    </div>
                    <ul className="space-y-2">
                      <li className="flex items-start space-x-2">
                        <span className="text-yellow-500 mt-1">•</span>
                        <span className="text-gray-600">The iframe will automatically adjust its height to fit the content</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-yellow-500 mt-1">•</span>
                        <span className="text-gray-600">Make sure to allow third-party iframes if you have Content Security Policy</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-yellow-500 mt-1">•</span>
                        <span className="text-gray-600">Some website builders may require special handling for iframe embeds</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-yellow-500 mt-1">•</span>
                        <span className="text-gray-600">The iframe includes responsive design, but you may need to adjust the container</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-5 border border-orange-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="p-2 bg-orange-500 rounded-lg">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <h4 className="font-bold text-orange-800">Important Note</h4>
                    </div>
                    <p className="text-orange-700 text-sm">
                      Iframes are more restrictive than the script loader method.
                      For advanced customization and better performance, consider using the script loader instead.
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="p-2 bg-green-500 rounded-lg">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <h4 className="font-bold text-green-800">Pro Tip</h4>
                    </div>
                    <p className="text-green-700 text-sm">
                      Start with a height of 600px and adjust based on your content.
                      The iframe will communicate with the parent window to resize automatically.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
